import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { FileText, Download, Users, Calendar, Euro, Building, Video, BarChart3, Shield, Edit, Upload, X, Save, Image, MessageSquare, Send, Phone, Key, Trash2, UserCog, Play, Youtube } from 'lucide-react';
import jsPDF from 'jspdf';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

const SPECIALTIES = [
  { id: 'therapeute', name: 'Thérapeute' },
  { id: 'naturopathe', name: 'Naturopathe' },
  { id: 'sophrologue', name: 'Sophrologue' },
  { id: 'psychologue', name: 'Psychologue' },
  { id: 'osteopathe', name: 'Ostéopathe' },
  { id: 'hypnotherapeute', name: 'Hypnothérapeute' },
  { id: 'coach', name: 'Coach' },
  { id: 'orthophoniste', name: 'Orthophoniste' },
];

const COUNTRIES = [
  { code: 'FR', name: '🇫🇷 France' },
  { code: 'UA', name: '🇺🇦 Ukraine' },
  { code: 'RU', name: '🇷🇺 Russie' },
  { code: 'IT', name: '🇮🇹 Italie' },
  { code: 'RO', name: '🇷🇴 Roumanie' },
  { code: 'GB', name: '🇬🇧 Royaume-Uni' },
  { code: 'IE', name: '🇮🇪 Irlande' },
  { code: 'DE', name: '🇩🇪 Allemagne' },
  { code: 'ES', name: '🇪🇸 Espagne' },
  { code: 'BE', name: '🇧🇪 Belgique' },
  { code: 'CH', name: '🇨🇭 Suisse' },
  { code: 'PL', name: '🇵🇱 Pologne' },
  { code: 'MD', name: '🇲🇩 Moldavie' },
  { code: 'BY', name: '🇧🇾 Biélorussie' },
  { code: 'CZ', name: '🇨🇿 Tchéquie' },
  { code: 'AT', name: '🇦🇹 Autriche' },
  { code: 'HU', name: '🇭🇺 Hongrie' },
  { code: 'NL', name: '🇳🇱 Pays-Bas' },
  { code: 'PT', name: '🇵🇹 Portugal' },
  { code: 'GR', name: '🇬🇷 Grèce' },
  { code: 'BG', name: '🇧🇬 Bulgarie' },
  { code: 'EE', name: '🇪🇪 Estonie' },
  { code: 'LV', name: '🇱🇻 Lettonie' },
  { code: 'LT', name: '🇱🇹 Lituanie' },
  { code: 'SE', name: '🇸🇪 Suède' },
  { code: 'DK', name: '🇩🇰 Danemark' },
  { code: 'NO', name: '🇳🇴 Norvège' },
  { code: 'FI', name: '🇫🇮 Finlande' },
];

const AdminBackoffice = () => {
  const navigate = useNavigate();
  const { token, isAuthenticated, user, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  
  const [practitioners, setPractitioners] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPractitioner, setSelectedPractitioner] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [reportData, setReportData] = useState(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  
  // Edit practitioner modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingPractitioner, setEditingPractitioner] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // SMS Settings
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [twilioConfigured, setTwilioConfigured] = useState(false);
  const [smsLogs, setSmsLogs] = useState([]);
  const [loadingSmsSettings, setLoadingSmsSettings] = useState(true);
  const [testPhone, setTestPhone] = useState('');
  const [sendingTestSms, setSendingTestSms] = useState(false);
  const [triggeringSms, setTriggeringSms] = useState(false);
  
  // Reset Password
  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [resetPasswordPractitioner, setResetPasswordPractitioner] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);

  // Create Practitioner
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    specialty: '',
    country: '',
    city: '',
    address: '',
    postal_code: '',
    price: '0',
    bio: ''
  });
  const [creating, setCreating] = useState(false);

  // Delete Practitioner
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const [practitionerToDelete, setPractitionerToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Clients Management
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [activeSection, setActiveSection] = useState('practitioners'); // 'practitioners', 'clients', 'videos'
  const [clientResetPasswordModalOpen, setClientResetPasswordModalOpen] = useState(false);
  const [clientToResetPassword, setClientToResetPassword] = useState(null);
  const [clientNewPassword, setClientNewPassword] = useState('');
  const [resettingClientPassword, setResettingClientPassword] = useState(false);

  // Featured Videos
  const [featuredVideos, setFeaturedVideos] = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [addVideoModalOpen, setAddVideoModalOpen] = useState(false);
  const [newVideo, setNewVideo] = useState({
    title: '',
    description: '',
    video_type: 'youtube',
    video_url: '',
    video_id: '',
    practitioner_id: '',
    display_date: ''
  });
  const [addingVideo, setAddingVideo] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoFile, setVideoFile] = useState(null);

  useEffect(() => {
    // Wait for auth to load
    if (authLoading) {
      return;
    }
    
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    
    // Check if user is admin
    if (user && !user.is_admin) {
      toast.error('Accès réservé à l\'administrateur');
      navigate('/client/dashboard');
      return;
    }

    const fetchData = async () => {
      try {
        const [practitionersRes, statsRes, settingsRes] = await Promise.all([
          axios.get(`${API}/admin/practitioners`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/admin/stats/overview`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/admin/settings`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} }))
        ]);
        setPractitioners(practitionersRes.data);
        setStats(statsRes.data);
        
        // SMS settings
        const settings = settingsRes.data || {};
        setSmsEnabled(settings.sms_reminders_enabled === 'true');
        setTwilioConfigured(settings.twilio_configured || false);
        setLoadingSmsSettings(false);
        
        // Fetch SMS logs
        try {
          const logsRes = await axios.get(`${API}/admin/sms-logs?limit=20`, { headers: { Authorization: `Bearer ${token}` } });
          setSmsLogs(logsRes.data || []);
        } catch (e) {
          console.log('SMS logs not available');
        }
      } catch (error) {
        if (error.response?.status === 403) {
          toast.error('Accès réservé à l\'administrateur');
          navigate('/');
        } else {
          toast.error('Erreur lors du chargement');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isAuthenticated, authLoading, user, navigate, token]);

  const openEditModal = (practitioner) => {
    setEditingPractitioner(practitioner);
    setEditForm({
      specialty: practitioner.specialty || '',
      bio: practitioner.bio || '',
      photo_url: practitioner.photo_url || '',
      country: practitioner.country || '',
      city: practitioner.city || '',
      address: practitioner.address || '',
      postal_code: practitioner.postal_code || '',
      price: practitioner.price?.toString() || '',
      years_experience: practitioner.years_experience?.toString() || '',
      diplomas: practitioner.diplomas?.join(', ') || '',
      detailed_description: practitioner.detailed_description || '',
      is_verified: practitioner.is_verified || false,
      subscription_enabled: practitioner.subscription_enabled || false
    });
    setEditModalOpen(true);
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editingPractitioner) return;

    // Only JPG and PNG allowed
    const allowedTypes = ['image/jpeg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non autorisé. Utilisez uniquement JPG ou PNG');
      return;
    }

    // Max 500 KB
    if (file.size > 500 * 1024) {
      toast.error('Fichier trop volumineux (max 500 Ko)');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API}/admin/practitioner/${editingPractitioner.id}/upload-photo`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setEditForm(prev => ({ ...prev, photo_url: response.data.photo_url }));
      toast.success('Photo uploadée avec succès');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const savePractitioner = async () => {
    if (!editingPractitioner) return;

    setSaving(true);
    try {
      const payload = {
        ...editForm,
        price: editForm.price ? parseFloat(editForm.price) : null,
        years_experience: editForm.years_experience ? parseInt(editForm.years_experience) : null,
        diplomas: editForm.diplomas ? editForm.diplomas.split(',').map(d => d.trim()).filter(d => d) : []
      };

      await axios.put(
        `${API}/admin/practitioner/${editingPractitioner.id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Refresh practitioners list
      const response = await axios.get(`${API}/admin/practitioners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPractitioners(response.data);

      toast.success('Praticien mis à jour avec succès');
      setEditModalOpen(false);
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // SMS Functions
  const toggleSmsEnabled = async (enabled) => {
    try {
      await axios.put(
        `${API}/admin/settings/sms_reminders_enabled`,
        { value: enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSmsEnabled(enabled);
      toast.success(enabled ? 'Rappels SMS activés' : 'Rappels SMS désactivés');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const sendTestSms = async () => {
    if (!testPhone) {
      toast.error('Veuillez entrer un numéro de téléphone');
      return;
    }
    
    setSendingTestSms(true);
    try {
      await axios.post(
        `${API}/admin/sms-test`,
        { phone: testPhone },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('SMS de test envoyé !');
      setTestPhone('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi du SMS');
    } finally {
      setSendingTestSms(false);
    }
  };

  const triggerSmsReminders = async () => {
    setTriggeringSms(true);
    try {
      await axios.post(
        `${API}/admin/sms-reminders/trigger`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Envoi des rappels SMS lancé pour les RDV de demain');
      
      // Refresh logs after a short delay
      setTimeout(async () => {
        try {
          const logsRes = await axios.get(`${API}/admin/sms-logs?limit=20`, { headers: { Authorization: `Bearer ${token}` } });
          setSmsLogs(logsRes.data || []);
        } catch (e) {}
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du déclenchement');
    } finally {
      setTriggeringSms(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setResettingPassword(true);
    try {
      await axios.post(
        `${API}/admin/practitioner/${resetPasswordPractitioner.id}/reset-password`,
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Mot de passe réinitialisé pour ${resetPasswordPractitioner.user?.first_name} ${resetPasswordPractitioner.user?.last_name}`);
      setResetPasswordModalOpen(false);
      setResetPasswordPractitioner(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réinitialisation');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleCreatePractitioner = async () => {
    if (!createForm.email || !createForm.password || !createForm.first_name || !createForm.last_name) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    if (createForm.password.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    
    setCreating(true);
    try {
      await axios.post(
        `${API}/admin/practitioner/create`,
        createForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Praticien ${createForm.first_name} ${createForm.last_name} créé avec succès !`);
      setCreateModalOpen(false);
      setCreateForm({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        phone: '',
        specialty: '',
        country: '',
        city: '',
        address: '',
        postal_code: '',
        price: '0',
        bio: ''
      });
      // Refresh practitioners list
      const practitionersRes = await axios.get(`${API}/admin/practitioners`, { headers: { Authorization: `Bearer ${token}` } });
      setPractitioners(practitionersRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du praticien');
    } finally {
      setCreating(false);
    }
  };

  // Delete Practitioner
  const handleDeletePractitioner = async () => {
    if (!practitionerToDelete) return;
    
    setDeleting(true);
    try {
      await axios.delete(
        `${API}/admin/practitioner/${practitionerToDelete.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Praticien ${practitionerToDelete.user?.first_name} ${practitionerToDelete.user?.last_name} supprimé`);
      setDeleteConfirmModalOpen(false);
      setPractitionerToDelete(null);
      // Refresh practitioners list
      const practitionersRes = await axios.get(`${API}/admin/practitioners`, { headers: { Authorization: `Bearer ${token}` } });
      setPractitioners(practitionersRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  // Fetch Clients
  const fetchClients = async () => {
    setLoadingClients(true);
    try {
      const response = await axios.get(`${API}/admin/clients`, { headers: { Authorization: `Bearer ${token}` } });
      setClients(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des clients');
    } finally {
      setLoadingClients(false);
    }
  };

  // Reset Client Password
  const handleResetClientPassword = async () => {
    if (!clientNewPassword || clientNewPassword.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setResettingClientPassword(true);
    try {
      await axios.post(
        `${API}/admin/client/${clientToResetPassword.id}/reset-password`,
        { new_password: clientNewPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Mot de passe réinitialisé pour ${clientToResetPassword.first_name} ${clientToResetPassword.last_name}`);
      setClientResetPasswordModalOpen(false);
      setClientToResetPassword(null);
      setClientNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réinitialisation');
    } finally {
      setResettingClientPassword(false);
    }
  };

  // Fetch Featured Videos
  const fetchFeaturedVideos = async () => {
    setLoadingVideos(true);
    try {
      const response = await axios.get(`${API}/admin/featured-videos`, { headers: { Authorization: `Bearer ${token}` } });
      setFeaturedVideos(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des vidéos');
    } finally {
      setLoadingVideos(false);
    }
  };

  // Add Featured Video
  const handleAddVideo = async () => {
    if (!newVideo.title) {
      toast.error('Le titre est requis');
      return;
    }
    
    setAddingVideo(true);
    try {
      let videoUrl = newVideo.video_url;
      
      // If there's a file to upload, upload it first
      if (videoFile) {
        setUploadingVideo(true);
        const formData = new FormData();
        formData.append('file', videoFile);
        
        const uploadResponse = await axios.post(
          `${API}/admin/upload-video`,
          formData,
          { 
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        videoUrl = uploadResponse.data.video_url;
        setUploadingVideo(false);
      }
      
      await axios.post(
        `${API}/admin/featured-videos`,
        { 
          ...newVideo, 
          video_type: videoFile ? 'upload' : 'url',
          video_url: videoUrl 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Vidéo ajoutée');
      setAddVideoModalOpen(false);
      setNewVideo({ title: '', description: '', video_type: 'upload', video_url: '', video_id: '', practitioner_id: '', display_date: '' });
      setVideoFile(null);
      fetchFeaturedVideos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout');
    } finally {
      setAddingVideo(false);
      setUploadingVideo(false);
    }
  };

  // Handle video file selection
  const handleVideoFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check file type
    const allowedTypes = ['video/mp4', 'video/webm', 'video/quicktime'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non autorisé. Utilisez MP4, WebM ou MOV');
      return;
    }
    
    // Check file size (100 MB max)
    if (file.size > 100 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 100 Mo)');
      return;
    }
    
    setVideoFile(file);
    toast.success(`Fichier sélectionné: ${file.name}`);
  };

  // Delete Video
  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm('Supprimer cette vidéo ?')) return;
    
    try {
      await axios.delete(`${API}/admin/featured-videos/${videoId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Vidéo supprimée');
      setFeaturedVideos(prev => prev.filter(v => v.id !== videoId));
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Toggle Video Active
  const handleToggleVideo = async (videoId) => {
    try {
      await axios.put(`${API}/admin/featured-videos/${videoId}/toggle`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setFeaturedVideos(prev => prev.map(v => v.id === videoId ? { ...v, is_active: !v.is_active } : v));
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const fetchReport = async () => {
    if (!selectedPractitioner) {
      toast.error('Veuillez sélectionner un praticien');
      return;
    }

    try {
      const response = await axios.get(
        `${API}/admin/practitioner/${selectedPractitioner}/report?month=${selectedMonth}&year=${selectedYear}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReportData(response.data);
    } catch (error) {
      toast.error('Erreur lors de la génération du rapport');
    }
  };

  const generatePDF = () => {
    if (!reportData) return;

    setGeneratingPDF(true);
    
    try {
      const doc = new jsPDF();
      const { practitioner, period, consultations, revenue, appointments } = reportData;
      
      // Header
      doc.setFontSize(20);
      doc.setTextColor(74, 103, 65); // Sage color
      doc.text('Opora Vie', 20, 20);
      
      doc.setFontSize(16);
      doc.setTextColor(45, 58, 58);
      doc.text('Rapport Mensuel', 20, 30);
      
      // Period
      doc.setFontSize(12);
      doc.setTextColor(100, 100, 100);
      doc.text(`${period.month_name} ${period.year}`, 20, 40);
      
      // Practitioner info
      doc.setFontSize(14);
      doc.setTextColor(45, 58, 58);
      doc.text('Informations Praticien', 20, 55);
      
      doc.setFontSize(10);
      doc.setTextColor(80, 80, 80);
      doc.text(`Nom: ${practitioner.name}`, 20, 65);
      doc.text(`Email: ${practitioner.email}`, 20, 72);
      doc.text(`Spécialité: ${practitioner.specialty}`, 20, 79);
      doc.text(`Ville: ${practitioner.city}`, 20, 86);
      doc.text(`Tarif consultation: ${practitioner.price}€`, 20, 93);
      
      // Consultations summary
      doc.setFontSize(14);
      doc.setTextColor(45, 58, 58);
      doc.text('Résumé des Consultations', 20, 110);
      
      doc.setFontSize(10);
      doc.setTextColor(80, 80, 80);
      doc.text(`Total: ${consultations.total}`, 20, 120);
      doc.text(`En cabinet: ${consultations.cabinet}`, 20, 127);
      doc.text(`En visio: ${consultations.visio}`, 20, 134);
      
      // Revenue summary
      doc.setFontSize(14);
      doc.setTextColor(45, 58, 58);
      doc.text('Revenus Générés', 120, 110);
      
      doc.setFontSize(10);
      doc.setTextColor(80, 80, 80);
      doc.text(`Total: ${revenue.total}€`, 120, 120);
      doc.text(`Cabinet: ${revenue.cabinet}€`, 120, 127);
      doc.text(`Visio: ${revenue.visio}€`, 120, 134);
      doc.text(`Payé en ligne: ${revenue.online_paid}€`, 120, 141);
      
      // Appointments table
      if (appointments.length > 0) {
        doc.setFontSize(14);
        doc.setTextColor(45, 58, 58);
        doc.text('Détail des Rendez-vous', 20, 160);
        
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        
        // Table header
        doc.text('Date', 20, 170);
        doc.text('Heure', 55, 170);
        doc.text('Type', 85, 170);
        doc.text('Statut', 115, 170);
        doc.text('Payé', 150, 170);
        
        // Table rows
        let y = 178;
        appointments.slice(0, 15).forEach((apt) => {
          doc.text(apt.date, 20, y);
          doc.text(apt.time, 55, y);
          doc.text(apt.type === 'cabinet' ? 'Cabinet' : 'Visio', 85, y);
          doc.text(apt.status, 115, y);
          doc.text(apt.paid ? 'Oui' : 'Non', 150, y);
          y += 7;
        });
        
        if (appointments.length > 15) {
          doc.text(`... et ${appointments.length - 15} autres rendez-vous`, 20, y + 5);
        }
      }
      
      // Footer
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`Généré le ${new Date().toLocaleDateString('fr-FR')} - Opora Vie`, 20, 285);
      
      // Save PDF
      doc.save(`rapport_${practitioner.name.replace(/\s+/g, '_')}_${period.month_name}_${period.year}.pdf`);
      toast.success('PDF généré avec succès');
    } catch (error) {
      toast.error('Erreur lors de la génération du PDF');
    } finally {
      setGeneratingPDF(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="loader" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-backoffice">
      <Navbar />
      
      <div className="container-app py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-terracotta-100 rounded-xl flex items-center justify-center">
              <Shield className="w-5 h-5 text-terracotta-600" />
            </div>
            <h1 className="font-heading text-3xl font-semibold text-foreground">
              Administration
            </h1>
          </div>
          <p className="text-muted-foreground">
            Gérez les praticiens et générez des rapports mensuels
          </p>
        </div>

        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="stat-card" data-testid="stat-practitioners">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Praticiens</span>
                <Users className="w-5 h-5 text-sage-500" />
              </div>
              <p className="text-2xl font-heading font-semibold">{stats.practitioners}</p>
            </div>
            <div className="stat-card" data-testid="stat-clients">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Clients</span>
                <Users className="w-5 h-5 text-terracotta-500" />
              </div>
              <p className="text-2xl font-heading font-semibold">{stats.clients}</p>
            </div>
            <div className="stat-card" data-testid="stat-appointments">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">RDV Total</span>
                <Calendar className="w-5 h-5 text-sage-500" />
              </div>
              <p className="text-2xl font-heading font-semibold">{stats.appointments.total}</p>
            </div>
            <div className="stat-card" data-testid="stat-revenue">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Revenus</span>
                <Euro className="w-5 h-5 text-green-500" />
              </div>
              <p className="text-2xl font-heading font-semibold text-green-600">{stats.revenue.total}€</p>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="mb-8 flex gap-4">
          <Button
            onClick={() => setCreateModalOpen(true)}
            className="btn-primary flex items-center gap-2"
            data-testid="create-practitioner-btn"
          >
            <Users className="w-4 h-4" />
            Créer un praticien
          </Button>
          <Button
            onClick={() => navigate('/admin/blog')}
            variant="outline"
            className="flex items-center gap-2"
            data-testid="manage-blog-btn"
          >
            <FileText className="w-4 h-4" />
            Gérer le Blog
          </Button>
        </div>

        {/* Report Generator */}
        <div className="bg-white rounded-2xl border border-sand-200 shadow-soft p-6 md:p-8 mb-8" data-testid="report-generator">
          <h2 className="font-heading text-xl font-medium mb-6 flex items-center gap-2">
            <FileText className="w-5 h-5 text-sage-600" />
            Générer un rapport PDF
          </h2>
          
          <div className="grid md:grid-cols-4 gap-4 mb-6">
            {/* Practitioner Select */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Praticien</label>
              <Select value={selectedPractitioner} onValueChange={setSelectedPractitioner}>
                <SelectTrigger className="rounded-xl border-sand-200" data-testid="select-practitioner">
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent>
                  {practitioners.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.user?.first_name} {p.user?.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Month Select */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Mois</label>
              <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                <SelectTrigger className="rounded-xl border-sand-200" data-testid="select-month">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map((month, index) => (
                    <SelectItem key={index + 1} value={(index + 1).toString()}>{month}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Year Select */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Année</label>
              <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                <SelectTrigger className="rounded-xl border-sand-200" data-testid="select-year">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026].map((year) => (
                    <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Generate Button */}
            <div className="flex items-end">
              <Button 
                onClick={fetchReport}
                className="w-full bg-sage-500 hover:bg-sage-600 rounded-xl"
                data-testid="generate-report-btn"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Générer
              </Button>
            </div>
          </div>

          {/* Report Preview */}
          {reportData && (
            <div className="border-t border-sand-200 pt-6 mt-6 animate-fadeIn" data-testid="report-preview">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading text-lg font-medium">
                  Rapport - {reportData.period.month_name} {reportData.period.year}
                </h3>
                <Button 
                  onClick={generatePDF}
                  disabled={generatingPDF}
                  className="bg-terracotta-500 hover:bg-terracotta-600 rounded-xl"
                  data-testid="download-pdf-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {generatingPDF ? 'Génération...' : 'Télécharger PDF'}
                </Button>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Practitioner Info */}
                <div className="bg-sand-50 rounded-xl p-5">
                  <h4 className="font-medium mb-3">{reportData.practitioner.name}</h4>
                  <p className="text-sm text-muted-foreground">{reportData.practitioner.email}</p>
                  <p className="text-sm text-muted-foreground capitalize">{reportData.practitioner.specialty}</p>
                  <p className="text-sm text-muted-foreground">{reportData.practitioner.city}</p>
                </div>
                
                {/* Summary */}
                <div className="bg-sand-50 rounded-xl p-5">
                  <h4 className="font-medium mb-3">Résumé</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Consultations</p>
                      <p className="text-xl font-semibold">{reportData.consultations.total}</p>
                      <div className="flex gap-4 text-xs text-muted-foreground mt-1">
                        <span className="flex items-center gap-1">
                          <Building className="w-3 h-3" /> {reportData.consultations.cabinet}
                        </span>
                        <span className="flex items-center gap-1">
                          <Video className="w-3 h-3" /> {reportData.consultations.visio}
                        </span>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Revenus</p>
                      <p className="text-xl font-semibold text-green-600">{reportData.revenue.total}€</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Dont {reportData.revenue.online_paid}€ payé en ligne
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Appointments List */}
              {reportData.appointments.length > 0 && (
                <div className="mt-6">
                  <h4 className="font-medium mb-3">Détail des rendez-vous ({reportData.appointments.length})</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-sand-100">
                        <tr>
                          <th className="text-left p-3 rounded-tl-lg">Date</th>
                          <th className="text-left p-3">Heure</th>
                          <th className="text-left p-3">Type</th>
                          <th className="text-left p-3">Statut</th>
                          <th className="text-left p-3 rounded-tr-lg">Payé</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.appointments.slice(0, 10).map((apt, index) => (
                          <tr key={index} className="border-b border-sand-100">
                            <td className="p-3">{apt.date}</td>
                            <td className="p-3">{apt.time}</td>
                            <td className="p-3">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
                                apt.type === 'cabinet' ? 'bg-sage-100 text-sage-700' : 'bg-terracotta-100 text-terracotta-700'
                              }`}>
                                {apt.type === 'cabinet' ? <Building className="w-3 h-3" /> : <Video className="w-3 h-3" />}
                                {apt.type === 'cabinet' ? 'Cabinet' : 'Visio'}
                              </span>
                            </td>
                            <td className="p-3 capitalize">{apt.status}</td>
                            <td className="p-3">
                              {apt.paid ? (
                                <span className="text-green-600">Oui</span>
                              ) : (
                                <span className="text-muted-foreground">Non</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {reportData.appointments.length > 10 && (
                      <p className="text-sm text-muted-foreground mt-2 text-center">
                        ... et {reportData.appointments.length - 10} autres rendez-vous
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* SMS Settings */}
        <div className="bg-white rounded-2xl border border-sand-200 shadow-soft p-6 md:p-8 mb-8" data-testid="sms-settings">
          <h2 className="font-heading text-xl font-medium mb-6 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-sage-600" />
            Rappels SMS (24h avant RDV)
          </h2>
          
          {/* Toggle SMS */}
          <div className="flex items-center justify-between p-4 bg-sand-50 rounded-xl mb-6">
            <div>
              <p className="font-medium">Activer les rappels SMS</p>
              <p className="text-sm text-muted-foreground">
                Envoi automatique d'un SMS aux clients 24h avant leur RDV
              </p>
            </div>
            <Switch
              checked={smsEnabled}
              onCheckedChange={toggleSmsEnabled}
              disabled={loadingSmsSettings}
              data-testid="sms-toggle"
            />
          </div>
          
          {/* Twilio Status */}
          <div className={`p-4 rounded-xl mb-6 ${twilioConfigured ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
            <div className="flex items-center gap-2">
              <Phone className={`w-5 h-5 ${twilioConfigured ? 'text-green-600' : 'text-amber-600'}`} />
              <span className={`font-medium ${twilioConfigured ? 'text-green-700' : 'text-amber-700'}`}>
                {twilioConfigured ? 'Twilio configuré' : 'Twilio non configuré'}
              </span>
            </div>
            {!twilioConfigured && (
              <p className="text-sm text-amber-600 mt-2">
                Pour activer les SMS, ajoutez vos clés Twilio dans le fichier .env :
                <code className="block mt-1 bg-white/50 p-2 rounded text-xs font-mono">
                  TWILIO_ACCOUNT_SID=votre_sid<br/>
                  TWILIO_AUTH_TOKEN=votre_token<br/>
                  TWILIO_PHONE_NUMBER=+33...
                </code>
              </p>
            )}
          </div>
          
          {/* Test SMS */}
          {twilioConfigured && (
            <div className="border border-sand-200 rounded-xl p-4 mb-6">
              <p className="font-medium mb-3">Envoyer un SMS de test</p>
              <div className="flex gap-2">
                <Input
                  type="tel"
                  placeholder="06 12 34 56 78"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  className="rounded-xl border-sand-200 flex-1"
                  data-testid="test-phone-input"
                />
                <Button
                  onClick={sendTestSms}
                  disabled={sendingTestSms || !testPhone}
                  className="btn-primary rounded-xl"
                  data-testid="send-test-sms"
                >
                  {sendingTestSms ? 'Envoi...' : 'Tester'}
                </Button>
              </div>
            </div>
          )}
          
          {/* Manual Trigger */}
          {twilioConfigured && smsEnabled && (
            <div className="flex items-center justify-between p-4 border border-sand-200 rounded-xl mb-6">
              <div>
                <p className="font-medium">Déclencher les rappels manuellement</p>
                <p className="text-sm text-muted-foreground">
                  Envoie les SMS pour tous les RDV de demain qui n'ont pas encore été rappelés
                </p>
              </div>
              <Button
                onClick={triggerSmsReminders}
                disabled={triggeringSms}
                variant="outline"
                className="rounded-xl"
                data-testid="trigger-sms"
              >
                <Send className="w-4 h-4 mr-2" />
                {triggeringSms ? 'Envoi...' : 'Envoyer maintenant'}
              </Button>
            </div>
          )}
          
          {/* SMS Logs */}
          {smsLogs.length > 0 && (
            <div>
              <h3 className="font-medium mb-3">Historique des SMS ({smsLogs.length})</h3>
              <div className="overflow-x-auto max-h-64 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-sand-100 sticky top-0">
                    <tr>
                      <th className="text-left p-2 rounded-tl-lg">Client</th>
                      <th className="text-left p-2">Téléphone</th>
                      <th className="text-left p-2">RDV</th>
                      <th className="text-left p-2">Statut</th>
                      <th className="text-left p-2 rounded-tr-lg">Date envoi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {smsLogs.map((log, idx) => (
                      <tr key={log.id || idx} className="border-b border-sand-100">
                        <td className="p-2">{log.first_name} {log.last_name}</td>
                        <td className="p-2 font-mono text-xs">{log.phone_number}</td>
                        <td className="p-2">{log.date} {log.start_time}</td>
                        <td className="p-2">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs ${
                            log.status === 'sent' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {log.status === 'sent' ? 'Envoyé' : 'Échec'}
                          </span>
                        </td>
                        <td className="p-2 text-muted-foreground text-xs">
                          {log.sent_at ? new Date(log.sent_at).toLocaleString('fr-FR') : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Section Tabs: Practitioners & Clients */}
        <div className="bg-white rounded-2xl border border-sand-200 shadow-soft p-6 md:p-8" data-testid="users-management">
          <div className="flex flex-wrap gap-4 mb-6">
            <Button
              variant={activeSection === 'practitioners' ? 'default' : 'outline'}
              onClick={() => setActiveSection('practitioners')}
              className={`rounded-xl ${activeSection === 'practitioners' ? 'bg-sage-500 hover:bg-sage-600' : ''}`}
              data-testid="tab-practitioners"
            >
              <Users className="w-4 h-4 mr-2" />
              Praticiens ({practitioners.length})
            </Button>
            <Button
              variant={activeSection === 'clients' ? 'default' : 'outline'}
              onClick={() => {
                setActiveSection('clients');
                if (clients.length === 0) fetchClients();
              }}
              className={`rounded-xl ${activeSection === 'clients' ? 'bg-sage-500 hover:bg-sage-600' : ''}`}
              data-testid="tab-clients"
            >
              <UserCog className="w-4 h-4 mr-2" />
              Clients
            </Button>
            <Button
              variant={activeSection === 'videos' ? 'default' : 'outline'}
              onClick={() => {
                setActiveSection('videos');
                if (featuredVideos.length === 0) fetchFeaturedVideos();
              }}
              className={`rounded-xl ${activeSection === 'videos' ? 'bg-sage-500 hover:bg-sage-600' : ''}`}
              data-testid="tab-videos"
            >
              <Play className="w-4 h-4 mr-2" />
              Vidéos à la Une
            </Button>
          </div>

          {/* Practitioners Section */}
          {activeSection === 'practitioners' && (
            <>
              <h2 className="font-heading text-xl font-medium mb-6">
                Liste des praticiens ({practitioners.length})
              </h2>
              
              {practitioners.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">Aucun praticien enregistré</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-sand-100">
                      <tr>
                        <th className="text-left p-3 rounded-tl-lg">Photo</th>
                        <th className="text-left p-3">Nom</th>
                        <th className="text-left p-3">Email</th>
                        <th className="text-left p-3">Spécialité</th>
                        <th className="text-left p-3">Ville</th>
                        <th className="text-left p-3">Tarif</th>
                        <th className="text-left p-3">Abon.</th>
                        <th className="text-left p-3 rounded-tr-lg">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                  {practitioners.map((p) => (
                    <tr key={p.id} className="border-b border-sand-100 hover:bg-sand-50">
                      <td className="p-3">
                        <div className="w-10 h-10 rounded-lg overflow-hidden bg-sand-100">
                          {p.photo_url ? (
                            <img 
                              src={p.photo_url.startsWith('/api/photos') || p.photo_url.startsWith('/uploads')
                                ? `${process.env.REACT_APP_BACKEND_URL}${p.photo_url}`
                                : p.photo_url
                              }
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Image className="w-4 h-4 text-sand-400" />
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="p-3 font-medium">{p.user?.first_name} {p.user?.last_name}</td>
                      <td className="p-3 text-muted-foreground">{p.user?.email}</td>
                      <td className="p-3 capitalize">{p.specialty}</td>
                      <td className="p-3">{p.city || '-'}</td>
                      <td className="p-3">{p.price ? `${p.price}€` : '-'}</td>
                      <td className="p-3">
                        {p.subscription_status === 'active' ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            Actif
                          </span>
                        ) : p.subscription_enabled ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                            Requis
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                            -
                          </span>
                        )}
                      </td>
                      <td className="p-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditModal(p)}
                          className="text-sage-600 hover:text-sage-700 hover:bg-sage-50"
                          data-testid={`edit-practitioner-${p.id}`}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Modifier
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            setResetPasswordPractitioner(p);
                            setResetPasswordModalOpen(true);
                          }}
                          className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 ml-2"
                          data-testid={`reset-password-${p.id}`}
                        >
                          <Key className="w-4 h-4 mr-1" />
                          MDP
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            setPractitionerToDelete(p);
                            setDeleteConfirmModalOpen(true);
                          }}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 ml-2"
                          data-testid={`delete-practitioner-${p.id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Suppr.
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
            </>
          )}

          {/* Clients Section */}
          {activeSection === 'clients' && (
            <>
              <h2 className="font-heading text-xl font-medium mb-6 flex items-center gap-2">
                <UserCog className="w-5 h-5 text-sage-600" />
                Liste des clients
              </h2>
              
              {loadingClients ? (
                <p className="text-muted-foreground text-center py-8">Chargement...</p>
              ) : clients.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">Aucun client enregistré</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-sand-100">
                      <tr>
                        <th className="text-left p-3 rounded-tl-lg">Nom</th>
                        <th className="text-left p-3">Email</th>
                        <th className="text-left p-3">Téléphone</th>
                        <th className="text-left p-3">Date d'inscription</th>
                        <th className="text-left p-3 rounded-tr-lg">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clients.map((client) => (
                        <tr key={client.id} className="border-b border-sand-100 hover:bg-sand-50">
                          <td className="p-3 font-medium">{client.first_name} {client.last_name}</td>
                          <td className="p-3 text-muted-foreground">{client.email}</td>
                          <td className="p-3">{client.phone || '-'}</td>
                          <td className="p-3">
                            {client.created_at ? new Date(client.created_at).toLocaleDateString('fr-FR') : '-'}
                          </td>
                          <td className="p-3">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                setClientToResetPassword(client);
                                setClientResetPasswordModalOpen(true);
                              }}
                              className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                              data-testid={`reset-client-password-${client.id}`}
                            >
                              <Key className="w-4 h-4 mr-1" />
                              Réinitialiser MDP
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {/* Videos Section */}
          {activeSection === 'videos' && (
            <>
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-xl font-medium flex items-center gap-2">
                  <Play className="w-5 h-5 text-sage-600" />
                  Vidéos à la Une
                </h2>
                <Button
                  onClick={() => setAddVideoModalOpen(true)}
                  className="bg-sage-500 hover:bg-sage-600 rounded-xl"
                  data-testid="add-video-btn"
                >
                  <Youtube className="w-4 h-4 mr-2" />
                  Ajouter une vidéo
                </Button>
              </div>
              
              {loadingVideos ? (
                <p className="text-muted-foreground text-center py-8">Chargement...</p>
              ) : featuredVideos.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">Aucune vidéo ajoutée</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {featuredVideos.map((video) => (
                    <div key={video.id} className="border border-sand-200 rounded-xl p-4 bg-sand-50">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="font-medium">{video.title}</h3>
                          {video.practitioner_name && (
                            <p className="text-sm text-muted-foreground">{video.practitioner_name}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={video.is_active}
                            onCheckedChange={() => handleToggleVideo(video.id)}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteVideo(video.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground space-y-1">
                        <p>Type: {video.video_type === 'youtube' ? 'YouTube' : 'Upload'}</p>
                        {video.video_id && <p>ID: {video.video_id}</p>}
                        {video.display_date && <p>Date d'affichage: {video.display_date}</p>}
                        <p className={video.is_active ? 'text-green-600' : 'text-red-600'}>
                          {video.is_active ? '✓ Active' : '✗ Inactive'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteConfirmModalOpen} onOpenChange={setDeleteConfirmModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Confirmer la suppression
            </DialogTitle>
          </DialogHeader>
          {practitionerToDelete && (
            <div className="space-y-4">
              <p>
                Êtes-vous sûr de vouloir supprimer le praticien <strong>{practitionerToDelete.user?.first_name} {practitionerToDelete.user?.last_name}</strong> ?
              </p>
              <p className="text-sm text-muted-foreground">
                Cette action est irréversible. Le compte utilisateur et toutes les données associées seront supprimés.
              </p>
              <div className="flex gap-3 justify-end">
                <Button 
                  variant="outline" 
                  onClick={() => setDeleteConfirmModalOpen(false)}
                  className="rounded-xl"
                >
                  Annuler
                </Button>
                <Button 
                  onClick={handleDeletePractitioner}
                  disabled={deleting}
                  className="bg-red-600 hover:bg-red-700 rounded-xl"
                  data-testid="confirm-delete-btn"
                >
                  {deleting ? 'Suppression...' : 'Supprimer'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Client Reset Password Modal */}
      <Dialog open={clientResetPasswordModalOpen} onOpenChange={setClientResetPasswordModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-orange-600" />
              Réinitialiser le mot de passe client
            </DialogTitle>
          </DialogHeader>
          {clientToResetPassword && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Réinitialiser le mot de passe pour <strong>{clientToResetPassword.first_name} {clientToResetPassword.last_name}</strong>
              </p>
              <div className="space-y-2">
                <Label>Nouveau mot de passe</Label>
                <Input
                  type="text"
                  value={clientNewPassword}
                  onChange={(e) => setClientNewPassword(e.target.value)}
                  placeholder="Minimum 6 caractères"
                  className="rounded-xl border-sand-200"
                  data-testid="client-new-password-input"
                />
              </div>
              <div className="flex gap-3 justify-end">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setClientResetPasswordModalOpen(false);
                    setClientNewPassword('');
                  }}
                  className="rounded-xl"
                >
                  Annuler
                </Button>
                <Button 
                  onClick={handleResetClientPassword}
                  disabled={resettingClientPassword || clientNewPassword.length < 6}
                  className="bg-orange-600 hover:bg-orange-700 rounded-xl"
                  data-testid="confirm-client-reset-btn"
                >
                  {resettingClientPassword ? 'En cours...' : 'Réinitialiser'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset Password Modal */}
      <Dialog open={resetPasswordModalOpen} onOpenChange={setResetPasswordModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-orange-600" />
              Réinitialiser le mot de passe
            </DialogTitle>
          </DialogHeader>

          {resetPasswordPractitioner && (
            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground">
                Réinitialiser le mot de passe pour <strong>{resetPasswordPractitioner.user?.first_name} {resetPasswordPractitioner.user?.last_name}</strong>
              </p>
              
              <div className="space-y-2">
                <Label htmlFor="new-password">Nouveau mot de passe</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimum 6 caractères"
                  className="rounded-xl"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setResetPasswordModalOpen(false);
                    setNewPassword('');
                  }}
                >
                  Annuler
                </Button>
                <Button 
                  onClick={handleResetPassword}
                  disabled={resettingPassword || !newPassword || newPassword.length < 6}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  {resettingPassword ? 'Réinitialisation...' : 'Réinitialiser'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Practitioner Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-sage-600" />
              Modifier le praticien
            </DialogTitle>
          </DialogHeader>

          {editingPractitioner && (
            <div className="space-y-6 py-4">
              {/* Photo Upload */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Image className="w-4 h-4" />
                  Photo de profil
                </Label>
                
                <div className="flex items-start gap-4">
                  <div className="w-24 h-24 rounded-xl border-2 border-dashed border-sand-300 overflow-hidden bg-sand-50 flex items-center justify-center">
                    {editForm.photo_url ? (
                      <img 
                        src={editForm.photo_url.startsWith('/api/photos') || editForm.photo_url.startsWith('/uploads')
                          ? `${process.env.REACT_APP_BACKEND_URL}${editForm.photo_url}`
                          : editForm.photo_url
                        }
                        alt="Aperçu"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Image className="w-8 h-8 text-sand-400" />
                    )}
                  </div>
                  
                  <div className="flex-1 space-y-2">
                    <label 
                      className={`inline-flex items-center gap-2 px-4 py-2 bg-sage-50 hover:bg-sage-100 text-sage-700 rounded-xl cursor-pointer transition-colors ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <Upload className="w-4 h-4" />
                      {uploading ? 'Upload en cours...' : 'Choisir une photo'}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png"
                        onChange={handlePhotoUpload}
                        disabled={uploading}
                        className="hidden"
                      />
                    </label>
                    <p className="text-xs text-muted-foreground">
                      JPG, PNG ou WebP. Max 5 MB.
                    </p>
                  </div>
                </div>
              </div>

              {/* Specialty */}
              <div className="space-y-2">
                <Label>Spécialité</Label>
                <Select value={editForm.specialty} onValueChange={(v) => setEditForm(prev => ({ ...prev, specialty: v }))}>
                  <SelectTrigger className="rounded-xl border-sand-200">
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {SPECIALTIES.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Bio */}
              <div className="space-y-2">
                <Label>Présentation courte</Label>
                <Textarea
                  placeholder="Brève description du praticien..."
                  value={editForm.bio}
                  onChange={(e) => setEditForm(prev => ({ ...prev, bio: e.target.value }))}
                  className="min-h-[80px] rounded-xl border-sand-200"
                />
              </div>

              {/* Detailed Description */}
              <div className="space-y-2">
                <Label>Description détaillée</Label>
                <Textarea
                  placeholder="Parcours, philosophie, méthodes de travail..."
                  value={editForm.detailed_description}
                  onChange={(e) => setEditForm(prev => ({ ...prev, detailed_description: e.target.value }))}
                  className="min-h-[120px] rounded-xl border-sand-200"
                />
              </div>

              {/* Country */}
              <div className="space-y-2">
                <Label>Pays</Label>
                <Select value={editForm.country} onValueChange={(v) => setEditForm(prev => ({ ...prev, country: v }))}>
                  <SelectTrigger className="rounded-xl border-sand-200">
                    <SelectValue placeholder="Sélectionner un pays..." />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Location */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Ville</Label>
                  <Input
                    value={editForm.city}
                    onChange={(e) => setEditForm(prev => ({ ...prev, city: e.target.value }))}
                    className="rounded-xl border-sand-200"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Code postal</Label>
                  <Input
                    value={editForm.postal_code}
                    onChange={(e) => setEditForm(prev => ({ ...prev, postal_code: e.target.value }))}
                    className="rounded-xl border-sand-200"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Adresse</Label>
                <Input
                  value={editForm.address}
                  onChange={(e) => setEditForm(prev => ({ ...prev, address: e.target.value }))}
                  className="rounded-xl border-sand-200"
                />
              </div>

              {/* Price & Experience */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tarif consultation (€)</Label>
                  <Input
                    type="number"
                    value={editForm.price}
                    onChange={(e) => setEditForm(prev => ({ ...prev, price: e.target.value }))}
                    className="rounded-xl border-sand-200"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Années d'expérience</Label>
                  <Input
                    type="number"
                    value={editForm.years_experience}
                    onChange={(e) => setEditForm(prev => ({ ...prev, years_experience: e.target.value }))}
                    className="rounded-xl border-sand-200"
                  />
                </div>
              </div>

              {/* Diplomas */}
              <div className="space-y-2">
                <Label>Diplômes et Certifications</Label>
                <Textarea
                  placeholder="Diplôme 1, Diplôme 2, Certification 3... (séparés par des virgules)"
                  value={editForm.diplomas}
                  onChange={(e) => setEditForm(prev => ({ ...prev, diplomas: e.target.value }))}
                  className="min-h-[60px] rounded-xl border-sand-200"
                />
              </div>

              {/* Subscription Toggle */}
              <div className="flex items-center justify-between p-4 bg-sage-50 rounded-xl">
                <div>
                  <p className="font-medium">Activer l'abonnement mensuel</p>
                  <p className="text-sm text-muted-foreground">
                    Demander à ce praticien de payer 50€/mois
                  </p>
                </div>
                <Switch
                  checked={editForm.subscription_enabled}
                  onCheckedChange={(checked) => setEditForm(prev => ({ ...prev, subscription_enabled: checked }))}
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-sand-200">
                <Button
                  variant="outline"
                  onClick={() => setEditModalOpen(false)}
                  className="rounded-xl"
                >
                  <X className="w-4 h-4 mr-2" />
                  Annuler
                </Button>
                <Button
                  onClick={savePractitioner}
                  disabled={saving}
                  className="bg-sage-500 hover:bg-sage-600 rounded-xl"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Practitioner Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Users className="w-5 h-5 text-sage-500" />
              Créer un nouveau praticien
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Account Info */}
            <div className="p-4 bg-sage-50 rounded-xl">
              <h3 className="font-medium mb-3 text-sage-700">Informations de connexion</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={createForm.email}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="email@exemple.com"
                    className="rounded-xl border-sand-200"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Mot de passe *</Label>
                  <Input
                    type="text"
                    value={createForm.password}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="Min. 8 caractères"
                    className="rounded-xl border-sand-200"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Personal Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Prénom *</Label>
                <Input
                  value={createForm.first_name}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, first_name: e.target.value }))}
                  className="rounded-xl border-sand-200"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Nom *</Label>
                <Input
                  value={createForm.last_name}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, last_name: e.target.value }))}
                  className="rounded-xl border-sand-200"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Téléphone</Label>
              <Input
                type="tel"
                value={createForm.phone}
                onChange={(e) => setCreateForm(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+33 6 12 34 56 78"
                className="rounded-xl border-sand-200"
              />
            </div>

            {/* Specialty */}
            <div className="space-y-2">
              <Label>Spécialité</Label>
              <Select value={createForm.specialty} onValueChange={(v) => setCreateForm(prev => ({ ...prev, specialty: v }))}>
                <SelectTrigger className="rounded-xl border-sand-200">
                  <SelectValue placeholder="Sélectionner une spécialité..." />
                </SelectTrigger>
                <SelectContent>
                  {SPECIALTIES.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Country */}
            <div className="space-y-2">
              <Label>Pays</Label>
              <Select value={createForm.country} onValueChange={(v) => setCreateForm(prev => ({ ...prev, country: v }))}>
                <SelectTrigger className="rounded-xl border-sand-200">
                  <SelectValue placeholder="Sélectionner un pays..." />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map((c) => (
                    <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Location */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Ville</Label>
                <Input
                  value={createForm.city}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, city: e.target.value }))}
                  className="rounded-xl border-sand-200"
                />
              </div>
              <div className="space-y-2">
                <Label>Code postal</Label>
                <Input
                  value={createForm.postal_code}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, postal_code: e.target.value }))}
                  className="rounded-xl border-sand-200"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Adresse</Label>
              <Input
                value={createForm.address}
                onChange={(e) => setCreateForm(prev => ({ ...prev, address: e.target.value }))}
                className="rounded-xl border-sand-200"
              />
            </div>

            {/* Price */}
            <div className="space-y-2">
              <Label>Tarif consultation (€)</Label>
              <Input
                type="number"
                value={createForm.price}
                onChange={(e) => setCreateForm(prev => ({ ...prev, price: e.target.value }))}
                className="rounded-xl border-sand-200"
              />
            </div>

            {/* Bio */}
            <div className="space-y-2">
              <Label>Biographie</Label>
              <Textarea
                value={createForm.bio}
                onChange={(e) => setCreateForm(prev => ({ ...prev, bio: e.target.value }))}
                placeholder="Courte description du praticien..."
                className="min-h-[80px] rounded-xl border-sand-200"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-sand-200">
              <Button
                variant="outline"
                onClick={() => setCreateModalOpen(false)}
                className="rounded-xl"
              >
                <X className="w-4 h-4 mr-2" />
                Annuler
              </Button>
              <Button
                onClick={handleCreatePractitioner}
                disabled={creating}
                className="bg-sage-500 hover:bg-sage-600 rounded-xl"
              >
                <Users className="w-4 h-4 mr-2" />
                {creating ? 'Création...' : 'Créer le praticien'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Video Modal */}
      <Dialog open={addVideoModalOpen} onOpenChange={setAddVideoModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Youtube className="w-5 h-5 text-red-600" />
              Ajouter une vidéo à la Une
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Titre *</Label>
              <Input
                value={newVideo.title}
                onChange={(e) => setNewVideo({ ...newVideo, title: e.target.value })}
                placeholder="Titre de la vidéo"
                className="rounded-xl border-sand-200"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={newVideo.description}
                onChange={(e) => setNewVideo({ ...newVideo, description: e.target.value })}
                placeholder="Description courte"
                className="rounded-xl border-sand-200"
              />
            </div>
            <div className="space-y-2">
              <Label>Fichier vidéo *</Label>
              <div className="border-2 border-dashed border-sand-300 rounded-xl p-4 text-center">
                <input
                  type="file"
                  accept="video/mp4,video/webm,video/quicktime"
                  onChange={handleVideoFileChange}
                  className="hidden"
                  id="video-upload"
                />
                <label 
                  htmlFor="video-upload" 
                  className="cursor-pointer flex flex-col items-center gap-2"
                >
                  <Upload className="w-8 h-8 text-sage-500" />
                  <span className="text-sm text-muted-foreground">
                    {videoFile ? videoFile.name : 'Cliquez pour sélectionner une vidéo'}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    MP4, WebM ou MOV - Max 100 Mo
                  </span>
                </label>
              </div>
              {uploadingVideo && (
                <div className="flex items-center gap-2 text-sm text-sage-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-sage-600"></div>
                  Upload en cours...
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Praticien associé (optionnel)</Label>
              <select
                value={newVideo.practitioner_id}
                onChange={(e) => setNewVideo({ ...newVideo, practitioner_id: e.target.value })}
                className="w-full h-10 rounded-xl border border-sand-200 px-3"
              >
                <option value="">-- Aucun --</option>
                {practitioners.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.user?.first_name} {p.user?.last_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Date d'affichage (optionnel)</Label>
              <Input
                type="date"
                value={newVideo.display_date}
                onChange={(e) => setNewVideo({ ...newVideo, display_date: e.target.value })}
                className="rounded-xl border-sand-200"
              />
              <p className="text-xs text-muted-foreground">
                Laissez vide pour afficher aléatoirement
              </p>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setAddVideoModalOpen(false)}
                className="rounded-xl"
              >
                Annuler
              </Button>
              <Button
                onClick={handleAddVideo}
                disabled={addingVideo || !newVideo.title || !videoFile}
                className="bg-sage-500 hover:bg-sage-600 rounded-xl"
              >
                {uploadingVideo ? 'Upload...' : addingVideo ? 'Ajout...' : 'Ajouter'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBackoffice;
